import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Formik } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import images from '../../assets/images';
import OTPField from '../../components/common/OTPField';
import { resetAuthSetup } from '../../services/auth-setup/auth-setup-service';
import { login, signInConfirm } from '../../services/login/login-service';
import { getProfile } from '../../services/user/user-service';
import { AppDispatch, store } from '../../store';
import { changeLanguage, logout, saveUserData, setCurrencyDetails, userDetail } from '../../store/actions/app.Actions';
import { Common } from '../../utils/constants';
import { AccountMigrationOTPSchema } from '../../validation/login/login-validation';
import UpdateSecurityModal from './UpdateSecurityModal';

function AuthenticatorOTP({ email, password, onChangeStage, setIsResetAuthMethod }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dispatch = useDispatch<AppDispatch>();

  const [isShowUpdateSecurityModal, setIsShowUpdateSecurityModal] = useState<boolean>(false);
  const [isSubmittingOTP, setIsSubmittingOTP] = useState<boolean>(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleNavigationAfterMFASetup = async () => {
    try {
      const response = await getProfile();

      const authSession = await fetchAuthSession();
      const authResponse = { ...response, authSession };

      if (response?.Data?.status?.code === Common.UserStatus.CANCELLED) {
        dispatch(logout()).then(() => {
          toast.error(t('login.toast.cancelled'), { autoClose: 15000 });
        });
        return;
      }

      dispatch(saveUserData({ ...authResponse, ...response.Data }));
      dispatch(changeLanguage(response?.Data?.language_preference));
      dispatch(setCurrencyDetails(response?.Data?.default_currency));
      dispatch(userDetail(response?.Data));
      moment.locale(store.getState().UserData?.language?.code);

      const downloadVATRedirect = sessionStorage.getItem('vat_receipt_path') || '';

      if (downloadVATRedirect) {
        sessionStorage.removeItem('vat_receipt_path');
        navigate(downloadVATRedirect);
        return;
      }
      if (!response?.Data.is_profile_verified) {
        navigate(Common.Routes.PROFILE_REDIRECT);
        return;
      }
      if (response?.Data.payment_type === null) {
        navigate(Common.Routes.PAYMENT);
        return;
      }
      navigate(Common.Routes.HOME);
    } catch (error) {
      toast.error(t('login.toast.error'));
      navigate(Common.Routes.HOME, { replace: true });
    }
  };

  const handleConfirmTOTP = async (code: string) => {
    setIsSubmittingOTP(true);
    try {
      document.getElementById('loading')?.classList.add('manual-loading');
      const response = await signInConfirm({ code });
      if (response?.isSignedIn && response.nextStep.signInStep === Common.AuthenticationSteps.DONE) {
        await handleNavigationAfterMFASetup();
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.name === Common.Authentication.CodeMismatchException) {
        toast.error(t('signup.toast.invalidCode'));
      } else if (error.name === Common.Authentication.ExpiredCodeException) {
        toast.error(t('signup.toast.expiredMFACode'));
      } else if (error.name === Common.Authentication.NotAuthorizedException) {
        onChangeStage(Common.AuthStage.SIGNIN);
      } else {
        toast.error(error.message.toString());
      }
    } finally {
      document.getElementById('loading')?.classList.remove('manual-loading');
      setIsSubmittingOTP(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await resetAuthSetup(email);

      if (!executeRecaptcha) return;

      try {
        document.getElementById('loading')?.classList.remove('spinner-disable');
        const token = await executeRecaptcha('login');
        await login(
          {
            email: email,
            password: password,
            recaptchaCode: token,
          },
          false,
          true,
        );
        document.getElementById('loading')?.classList.add('spinner-disable');

        onChangeStage(Common.AuthStage.SIGNIN_OTP);
        setIsResetAuthMethod(true);
      } catch {
        onChangeStage(Common.AuthStage.SIGNIN);
      }

      const params = {
        isUseDifferentMethod: true,
        email,
      };
      localStorage.setItem(Common.RESET_AUTH_METHOD, JSON.stringify(params));
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.toString());
      }
    }
  };

  const closeModal = () => {
    setIsShowUpdateSecurityModal(false);
  };

  return (
    <div className="auth-options-container">
      <div className="auth-header">
        <div className="auth-icon-container">
          <img src={images.authAppIconSVG} alt="Security" className="auth-icon-image" />
        </div>
        <h1 className="auth-title">{t('authentication.authAppOTP.header')}</h1>
        <p className="auth-subtitle sign-in-otp-subtitle">{t('authentication.authAppOTP.subHeader')}</p>
      </div>

      <Formik
        initialValues={{ code: '' }}
        onSubmit={async (values) => {
          if (isSubmittingOTP) return;
          await handleConfirmTOTP(values.code);
        }}
        validationSchema={AccountMigrationOTPSchema(t)}
      >
        {({ handleSubmit }) => (
          <form
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && isSubmittingOTP) {
                event.preventDefault();
              }
            }}
          >
            <div className="app-setup-otp-container p-1 mb-3">
              <OTPField name="code" customStyleClass="app-setup-otp" />
            </div>

            <div className="auth-app-button-OTP-wrapper">
              <button type="submit" className="btn btn-primary auth-app-button" disabled={isSubmittingOTP}>
                {t('authentication.button.continue')}
              </button>
              <p className="different-auth-method" onClick={() => setIsShowUpdateSecurityModal(true)}>
                {t('authentication.button.differentMethod')}
              </p>
            </div>
          </form>
        )}
      </Formik>

      {isShowUpdateSecurityModal && (
        <UpdateSecurityModal
          isShowUpdateSecurityModal={isShowUpdateSecurityModal}
          handleSubmit={handleSubmit}
          closeModal={closeModal}
          showBackButton={true}
        />
      )}
    </div>
  );
}

export default AuthenticatorOTP;
